import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import config from "../coreFIles/config";
import { getAdminAuthToken } from "../coreFIles/auth";
import CKEditorWithSource from "./CKEditorWithSource";
import {
  adminGlobalSettingCreateAction,
  adminGlobalSettingUpdateAction,
  adminGlobalSettingsListAction,
  adminGlobalSettingsUploadLogoAction,
  adminAppVersionUpdateAction,
  adminGlobalSettingsUploadIntroSupportFileAction
} from "../Action/action";

const tabs = ["General", "Force Update", "Legal", "Support"];
const APP_VERSION_RE = /^\d+(\.\d+){0,3}([+-][A-Za-z0-9.-]+)?$/;

function isValidAppVersion(value) {
  const s = String(value || "").trim();
  if (!s) return true;
  return s.length <= 32 && APP_VERSION_RE.test(s);
}

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function buildApiUrl(endpoint) {
  const base = (config.apiUrl || "").replace(/\/+$/, "");
  const path = String(endpoint || "").replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

class CkUploadAdapter {
  constructor(loader) {
    this.loader = loader;
    this.controller = new AbortController();
  }

  abort() {
    try {
      this.controller.abort();
    } catch (e) {}
  }

  async upload() {
    const file = await this.loader.file;
    const fd = new FormData();
    fd.append("upload", file);

    const token = getAdminAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
      headers,
      withCredentials: true,
      signal: this.controller.signal
    });

    const url = resp?.data?.url;
    if (!url) throw new Error("Upload failed");
    return { default: resolvePublicUrl(url) };
  }
}

function CkUploadAdapterPlugin(editor) {
  // eslint-disable-next-line no-param-reassign
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new CkUploadAdapter(loader);
}

function toBool(v, fallback = false) {
  if (v === undefined || v === null || v === "") return fallback;
  if (typeof v === "boolean") return v;
  const s = String(v).trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "on";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

/** Stored "YYYY-MM-DD[ HH:MM[:SS]]" → datetime-local "YYYY-MM-DDTHH:MM" */
function toDatetimeLocalValue(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}):(\d{2})(?::\d{2})?)?/);
  if (!m) return "";
  const hh = m[2] != null ? m[2] : "00";
  const mm = m[3] != null ? m[3] : "00";
  return `${m[1]}T${hh}:${mm}`;
}

/** datetime-local value → stored "YYYY-MM-DD HH:MM:SS" */
function fromDatetimeLocalValue(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return s.replace("T", " ").substring(0, 19);
  const ss = m[4] != null ? m[4] : "00";
  return `${m[1]} ${m[2]}:${m[3]}:${ss}`;
}

/** Stored version / date → date input "YYYY-MM-DD" */
function toDateInputValue(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
}

export default function Settings() {
  const [tab, setTab] = useState("General");

  const [appName, setAppName] = useState("Kids Life Coaching");
  const [tagline, setTagline] = useState("Brave. Engage. Bold. Courageous.");
  const [contactEmail, setContactEmail] = useState("info@kidslifecoaching.com");
  const [contactPhone, setContactPhone] = useState("+27 11 234 5678");

  const [facebookUrl, setFacebookUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [tiktokUrl, setTiktokUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");

  const [appLogo, setAppLogo] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const forceUpdateImageInputRef = useRef(null);

  const [forceUpdate, setForceUpdate] = useState(true);
  const [iosCurrentVersion, setIosCurrentVersion] = useState("");
  const [androidCurrentVersion, setAndroidCurrentVersion] = useState("");
  const [forceUpdateTitle, setForceUpdateTitle] = useState("Update required");
  const [forceUpdateDescription, setForceUpdateDescription] = useState("Please update to continue using the app.");
  const [forceUpdateButtonTitle, setForceUpdateButtonTitle] = useState("Update now");
  const [forceUpdateImageUrl, setForceUpdateImageUrl] = useState("");
  const [iosStoreUrl, setIosStoreUrl] = useState("");
  const [androidStoreUrl, setAndroidStoreUrl] = useState("");
  const [uploadingForceUpdateImage, setUploadingForceUpdateImage] = useState(false);

  const [primaryColor, setPrimaryColor] = useState("#2B7BCA");
  const [accentColor, setAccentColor] = useState("#E8723A");

  const [pushEnabled, setPushEnabled] = useState(true);
  const [weeklyUnlock, setWeeklyUnlock] = useState(true);
  const [hackAlerts, setHackAlerts] = useState(true);
  const [journalReminders, setJournalReminders] = useState(false);

  const [supportEmail, setSupportEmail] = useState("support@kidslifecoaching.com");
  const [supportPhone, setSupportPhone] = useState("+27 11 234 5679");
  const [bookCallLink, setBookCallLink] = useState("");
  const [introVideosSupportButtonText, setIntroVideosSupportButtonText] = useState(
    "Parent Support - When your child doesn’t want to watch →"
  );
  const [introVideosSupportFileUrl, setIntroVideosSupportFileUrl] = useState("");
  const [uploadingIntroSupportFile, setUploadingIntroSupportFile] = useState(false);
  const introSupportFileInputRef = useRef(null);
  const [weekUnlockDayHeading, setWeekUnlockDayHeading] = useState("Choose Your Start Day");
  const [weekUnlockDayBody, setWeekUnlockDayBody] = useState(
    "When would you like your journey to begin?\n\nYour weekly lessons unlock automatically every 7 days.\n\nChoose the weekly start date that works best for your family.\n\nPlease note you'll have access to the course from now on but the day selected is the reset trigger for the following weeks."
  );

  const [launchTermsEffectiveAt, setLaunchTermsEffectiveAt] = useState("2026-11-01 00:00:00");
  const [launchTermsVersion, setLaunchTermsVersion] = useState("2026-11-01");
  const [launchTermsTitle, setLaunchTermsTitle] = useState("Updated Terms & Privacy");
  const [launchTermsBody, setLaunchTermsBody] = useState(
    "We've updated our Terms of Service and Privacy Policy ahead of launch. Please take a moment to read them. They now cover everything about the course, your one-time purchase, and how we look after your family's information."
  );
  const [launchTermsCheckboxLabel, setLaunchTermsCheckboxLabel] = useState(
    "I agree to the updated Terms of Service and I have read the updated Privacy Policy."
  );
  const [launchTermsContinueLabel, setLaunchTermsContinueLabel] = useState("Continue");

  const [courseWelcomeDisclaimerTitle, setCourseWelcomeDisclaimerTitle] = useState("Important disclaimer");
  const [courseWelcomeDisclaimerBody, setCourseWelcomeDisclaimerBody] = useState("");
  const [courseWelcomeDisclaimerContinueLabel, setCourseWelcomeDisclaimerContinueLabel] = useState("Continue");

  const [termsAndConditionsUrl, setTermsAndConditionsUrl] = useState("");
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState("");
  const [helpAndSupportUrl, setHelpAndSupportUrl] = useState("");
  const [disclaimerUrl, setDisclaimerUrl] = useState("");

  const [ageGroups, setAgeGroups] = useState([
    { label: "Ages 4-7", range: "4 — 7 years" },
    { label: "Ages 8-12", range: "8 — 12 years" },
    { label: "Ages 13+", range: "13+ years" }
  ]);

  const [ageModalOpen, setAgeModalOpen] = useState(false);
  const [ageEditIndex, setAgeEditIndex] = useState(-1);
  const [ageDraft, setAgeDraft] = useState({ label: "", range: "" });

  const [existingByKey, setExistingByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminGlobalSettingsListAction();
      if (!res?.success) throw new Error(res?.message || res?.msg || "Failed to load settings");
      const rows = Array.isArray(res.data) ? res.data : [];
      const map = {};
      rows.forEach((r) => {
        const k = String(r?.setting_key || "").toUpperCase();
        if (!k) return;
        map[k] = { id: r?.id, value: r?.setting_value, created_at: r?.created_at, updated_at: r?.updated_at };
      });
      setExistingByKey(map);

      setAppName(map.APP_NAME?.value ?? "Kids Life Coaching");
      setTagline(map.TAGLINE?.value ?? "Brave. Engage. Bold. Courageous.");
      setContactEmail(map.CONTACT_EMAIL?.value ?? "info@kidslifecoaching.com");
      setContactPhone(map.CONTACT_PHONE?.value ?? "");

      setFacebookUrl(map.FACEBOOK_URL?.value ?? "");
      setInstagramUrl(map.INSTAGRAM_URL?.value ?? "");
      setYoutubeUrl(map.YOUTUBE_URL?.value ?? "");
      setTiktokUrl(map.TIKTOK_URL?.value ?? "");
      setWebsiteUrl(map.WEBSITE_URL?.value ?? "");

      setAppLogo(map.APP_LOGO?.value ?? "");
      setPrimaryColor(map.PRIMARY_COLOR?.value ?? "#2B7BCA");
      setAccentColor(map.ACCENT_COLOR?.value ?? "#E8723A");

      setForceUpdate(toBool(map.FORCE_UPDATE?.value, true));
      setIosCurrentVersion(map.IOS_CURRENT_VERSION?.value ?? "");
      setAndroidCurrentVersion(map.ANDROID_CURRENT_VERSION?.value ?? "");
      setForceUpdateTitle(map.FORCE_UPDATE_TITLE?.value || "Update required");
      setForceUpdateDescription(map.FORCE_UPDATE_DESCRIPTION?.value || "Please update to continue using the app.");
      setForceUpdateButtonTitle(map.FORCE_UPDATE_BUTTON_TITLE?.value || "Update now");
      setForceUpdateImageUrl(map.FORCE_UPDATE_IMAGE_URL?.value ?? "");
      setIosStoreUrl(map.IOS_STORE_URL?.value ?? "");
      setAndroidStoreUrl(map.ANDROID_STORE_URL?.value ?? "");

      setPushEnabled(toBool(map.ENABLE_PUSH_NOTIFICATION?.value, true));
      setWeeklyUnlock(toBool(map.WEEKLY_UNLOCK_REMINDERS?.value, true));
      setHackAlerts(toBool(map.NEW_COACHING_HACK_ALERTS?.value, true));
      setJournalReminders(toBool(map.JOURNAL_REMINDERS?.value, false));

      setSupportEmail(map.SUPPORT_EMAIL?.value ?? "");
      setSupportPhone(map.SUPPORT_PHONE?.value ?? "");
      setBookCallLink(map.BOOK_A_CALL_LINK?.value ?? "");
      setIntroVideosSupportButtonText(
        map.INTRO_VIDEOS_SUPPORT_BUTTON_TEXT?.value || "Parent Support - When your child doesn’t want to watch →"
      );
      setIntroVideosSupportFileUrl(map.INTRO_VIDEOS_SUPPORT_FILE_URL?.value ?? "");
      setWeekUnlockDayHeading(map.WEEK_UNLOCK_DAY_HEADING?.value ?? "Choose Your Start Day");
      setWeekUnlockDayBody(
        map.WEEK_UNLOCK_DAY_BODY?.value ??
        "When would you like your journey to begin?\n\nYour weekly lessons unlock automatically every 7 days.\n\nChoose the weekly start date that works best for your family.\n\nPlease note you'll have access to the course from now on but the day selected is the reset trigger for the following weeks."
      );

      setLaunchTermsEffectiveAt(map.LAUNCH_TERMS_EFFECTIVE_AT?.value ?? "2026-11-01 00:00:00");
      setLaunchTermsVersion(map.LAUNCH_TERMS_VERSION?.value ?? "2026-11-01");
      setLaunchTermsTitle(map.LAUNCH_TERMS_TITLE?.value ?? "Updated Terms & Privacy");
      setLaunchTermsBody(
        map.LAUNCH_TERMS_BODY?.value ??
          "We've updated our Terms of Service and Privacy Policy ahead of launch. Please take a moment to read them. They now cover everything about the course, your one-time purchase, and how we look after your family's information."
      );
      setLaunchTermsCheckboxLabel(
        map.LAUNCH_TERMS_CHECKBOX_LABEL?.value ??
          "I agree to the updated Terms of Service and I have read the updated Privacy Policy."
      );
      setLaunchTermsContinueLabel(map.LAUNCH_TERMS_CONTINUE_LABEL?.value ?? "Continue");

      setCourseWelcomeDisclaimerTitle(map.COURSE_WELCOME_DISCLAIMER_TITLE?.value ?? "Important disclaimer");
      setCourseWelcomeDisclaimerBody(map.COURSE_WELCOME_DISCLAIMER_BODY?.value ?? "");
      setCourseWelcomeDisclaimerContinueLabel(map.COURSE_WELCOME_DISCLAIMER_CONTINUE_LABEL?.value ?? "Continue");

      setTermsAndConditionsUrl(map.TERMS_AND_CONDITIONS_URL?.value ?? "");
      setPrivacyPolicyUrl(map.PRIVACY_POLICY_URL?.value ?? "");
      setHelpAndSupportUrl(map.HELP_AND_SUPPORT_URL?.value ?? "");
      setDisclaimerUrl(map.DISCLAIMER_URL?.value ?? "");

      // AGE_GROUP_CATEGORIES stored as JSON string [{label,range},...]
      const ageRaw = map.AGE_GROUP_CATEGORIES?.value;
      if (ageRaw) {
        try {
          const parsed = JSON.parse(String(ageRaw));
          if (Array.isArray(parsed)) {
            const cleaned = parsed
              .map((x) => ({
                label: String(x?.label || "").trim(),
                range: String(x?.range || "").trim()
              }))
              .filter((x) => x.label && x.range);
            if (cleaned.length) setAgeGroups(cleaned);
          }
        } catch (e) {}
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upsert = async (setting_key, setting_value) => {
    const key = String(setting_key || "").toUpperCase();
    if (!key) return;
    const existing = existingByKey?.[key];
    if (existing?.id) {
      return await adminGlobalSettingUpdateAction(existing.id, { setting_value });
    }
    return await adminGlobalSettingCreateAction({ setting_key: key, setting_value });
  };

  const saveAll = async () => {
    if (!isValidAppVersion(iosCurrentVersion) || !isValidAppVersion(androidCurrentVersion)) {
      toast.error("iOS and Android versions must look like 1.7.0 (or be left blank)", { toastId: "settings-save" });
      return;
    }
    setSaving(true);
    setError("");
    try {
      const entries = [
        ["APP_NAME", appName],
        ["TAGLINE", tagline],
        ["CONTACT_EMAIL", contactEmail],
        ["CONTACT_PHONE", contactPhone],
        ["FACEBOOK_URL", facebookUrl],
        ["INSTAGRAM_URL", instagramUrl],
        ["YOUTUBE_URL", youtubeUrl],
        ["TIKTOK_URL", tiktokUrl],
        ["WEBSITE_URL", websiteUrl],
        ["APP_LOGO", appLogo],
        ["PRIMARY_COLOR", primaryColor],
        ["ACCENT_COLOR", accentColor],
        ["ENABLE_PUSH_NOTIFICATION", pushEnabled ? "1" : "0"],
        ["WEEKLY_UNLOCK_REMINDERS", weeklyUnlock ? "1" : "0"],
        ["NEW_COACHING_HACK_ALERTS", hackAlerts ? "1" : "0"],
        ["JOURNAL_REMINDERS", journalReminders ? "1" : "0"],
        ["SUPPORT_EMAIL", supportEmail],
        ["SUPPORT_PHONE", supportPhone],
        ["BOOK_A_CALL_LINK", bookCallLink],
        ["INTRO_VIDEOS_SUPPORT_BUTTON_TEXT", introVideosSupportButtonText],
        ["INTRO_VIDEOS_SUPPORT_FILE_URL", introVideosSupportFileUrl],
        ["WEEK_UNLOCK_DAY_HEADING", weekUnlockDayHeading],
        ["WEEK_UNLOCK_DAY_BODY", weekUnlockDayBody],
        ["LAUNCH_TERMS_EFFECTIVE_AT", launchTermsEffectiveAt],
        ["LAUNCH_TERMS_VERSION", launchTermsVersion],
        ["LAUNCH_TERMS_TITLE", launchTermsTitle],
        ["LAUNCH_TERMS_BODY", launchTermsBody],
        ["LAUNCH_TERMS_CHECKBOX_LABEL", launchTermsCheckboxLabel],
        ["LAUNCH_TERMS_CONTINUE_LABEL", launchTermsContinueLabel],
        ["COURSE_WELCOME_DISCLAIMER_TITLE", courseWelcomeDisclaimerTitle],
        ["COURSE_WELCOME_DISCLAIMER_BODY", courseWelcomeDisclaimerBody],
        ["COURSE_WELCOME_DISCLAIMER_CONTINUE_LABEL", courseWelcomeDisclaimerContinueLabel],
        ["TERMS_AND_CONDITIONS_URL", termsAndConditionsUrl],
        ["PRIVACY_POLICY_URL", privacyPolicyUrl],
        ["HELP_AND_SUPPORT_URL", helpAndSupportUrl],
        ["DISCLAIMER_URL", disclaimerUrl],
        ["AGE_GROUP_CATEGORIES", JSON.stringify(ageGroups)],
        ["FORCE_UPDATE", forceUpdate ? "1" : "0"],
        ["IOS_CURRENT_VERSION", String(iosCurrentVersion || "").trim()],
        ["ANDROID_CURRENT_VERSION", String(androidCurrentVersion || "").trim()],
        ["FORCE_UPDATE_TITLE", forceUpdateTitle],
        ["FORCE_UPDATE_DESCRIPTION", forceUpdateDescription],
        ["FORCE_UPDATE_BUTTON_TITLE", forceUpdateButtonTitle],
        ["FORCE_UPDATE_IMAGE_URL", forceUpdateImageUrl],
        ["IOS_STORE_URL", iosStoreUrl],
        ["ANDROID_STORE_URL", androidStoreUrl]
      ];

      const results = await Promise.all(entries.map(([k, v]) => upsert(k, v)));
      const anyFailed = results.some((r) => r && r.success === false);
      if (anyFailed) throw new Error("Some settings failed to save");

      await loadSettings();
      toast.success("Settings saved", { toastId: "settings-save" });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to save settings.");
      toast.error("Save failed", { toastId: "settings-save" });
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("app_logo", file);
      const res = await adminGlobalSettingsUploadLogoAction(fd);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Upload failed");
      const record = res?.data;
      const newUrl = record?.setting_value || "";
      setAppLogo(newUrl);
      setExistingByKey((prev) => ({
        ...(prev || {}),
        APP_LOGO: { id: record?.id || prev?.APP_LOGO?.id, value: newUrl }
      }));
      toast.success("Logo uploaded", { toastId: "settings-logo" });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Upload failed", { toastId: "settings-logo" });
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const uploadIntroSupportFile = async (file) => {
    if (!file) return;
    setUploadingIntroSupportFile(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", "document");
      const res = await adminGlobalSettingsUploadIntroSupportFileAction(fd);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Upload failed");
      const record = res?.data;
      const newUrl = record?.setting_value || "";
      setIntroVideosSupportFileUrl(newUrl);
      setExistingByKey((prev) => ({
        ...(prev || {}),
        INTRO_VIDEOS_SUPPORT_FILE_URL: { id: record?.id || prev?.INTRO_VIDEOS_SUPPORT_FILE_URL?.id, value: newUrl }
      }));
      toast.success("Parent support file uploaded", { toastId: "settings-intro-support-file" });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Upload failed", { toastId: "settings-intro-support-file" });
    } finally {
      setUploadingIntroSupportFile(false);
      if (introSupportFileInputRef.current) introSupportFileInputRef.current.value = "";
    }
  };

  const uploadForceUpdateImage = async (file) => {
    if (!file) return;
    setUploadingForceUpdateImage(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await adminAppVersionUpdateAction(fd);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Upload failed");
      const newUrl = res?.data?.image_url || "";
      let relative = newUrl;
      try {
        relative = new URL(newUrl).pathname || newUrl;
      } catch (e) {
        relative = newUrl;
      }
      setForceUpdateImageUrl(relative);
      setExistingByKey((prev) => ({
        ...(prev || {}),
        FORCE_UPDATE_IMAGE_URL: { id: prev?.FORCE_UPDATE_IMAGE_URL?.id, value: relative }
      }));
      toast.success("Force update image uploaded", { toastId: "settings-force-update-image" });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Upload failed", { toastId: "settings-force-update-image" });
    } finally {
      setUploadingForceUpdateImage(false);
      if (forceUpdateImageInputRef.current) forceUpdateImageInputRef.current.value = "";
    }
  };

  const showLeft = tab === "General" || tab === "Legal" || tab === "Force Update" || tab === "Support";

  const lastUpdatedForKey = useCallback(
    (key) => {
      const k = String(key || "").toUpperCase();
      const rec = existingByKey?.[k];
      const ts = rec?.updated_at || rec?.created_at;
      return formatDate(ts);
    },
    [existingByKey]
  );

  const openAddAgeGroup = useCallback(() => {
    setAgeEditIndex(-1);
    setAgeDraft({ label: "", range: "" });
    setAgeModalOpen(true);
  }, []);

  const openEditAgeGroup = useCallback(
    (idx) => {
      const row = ageGroups?.[idx];
      if (!row) return;
      setAgeEditIndex(idx);
      setAgeDraft({ label: row.label || "", range: row.range || "" });
      setAgeModalOpen(true);
    },
    [ageGroups]
  );

  const saveAgeGroupDraft = () => {
    const label = String(ageDraft?.label || "").trim();
    const range = String(ageDraft?.range || "").trim();
    if (!label || !range) {
      toast.error("Label and Age Range are required", { toastId: "settings-agegroup-req" });
      return;
    }
    setAgeGroups((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const nextRow = { label, range };
      if (ageEditIndex >= 0 && ageEditIndex < arr.length) arr[ageEditIndex] = nextRow;
      else arr.push(nextRow);
      return arr;
    });
    setAgeModalOpen(false);
  };

  const left = useMemo(() => {
    if (tab === "Force Update") {
      return (
        <div className="card mb-6">
          <div className="settings-section">
            <h3>Force App Update</h3>
            <p className="small-text" style={{ marginBottom: 16 }}>
              Set versions only after the build is live on the App Store / Play Store. The app compares its installed version to these values and shows a blocking modal if it is older.
            </p>

            <div className="toggle-row">
              <div className="toggle-label">
                <h4>Force update</h4>
                <p>When on, the app blocks users on an older version</p>
              </div>
              <button
                type="button"
                className={`toggle-switch ${forceUpdate ? "on" : ""}`}
                onClick={() => setForceUpdate((prev) => !prev)}
                aria-label="Force update"
                style={{ border: "none" }}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>iOS current version</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="1.7.0"
                  value={iosCurrentVersion}
                  onChange={(e) => setIosCurrentVersion(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Android current version</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="1.7.0"
                  value={androidCurrentVersion}
                  onChange={(e) => setAndroidCurrentVersion(e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Modal title</label>
              <input type="text" className="form-control" value={forceUpdateTitle} onChange={(e) => setForceUpdateTitle(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Modal description</label>
              <textarea
                className="form-control"
                rows={3}
                value={forceUpdateDescription}
                onChange={(e) => setForceUpdateDescription(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Button title</label>
              <input type="text" className="form-control" value={forceUpdateButtonTitle} onChange={(e) => setForceUpdateButtonTitle(e.target.value)} />
            </div>
          </div>
        </div>
      );
    }

    if (tab === "Support") {
      return (
        <>
        <div className="card mb-6">
          <div className="settings-section">
            <h3>Intro Videos parent support</h3>
            <p className="small-text" style={{ marginBottom: 16 }}>
              Shown only on Intro Week → Intro Videos. Change the button label and PDF/media link here.
            </p>
            <div className="form-group">
              <label>Button text</label>
              <input
                type="text"
                className="form-control"
                value={introVideosSupportButtonText}
                onChange={(e) => setIntroVideosSupportButtonText(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>PDF / media URL</label>
              <input
                type="text"
                className="form-control"
                placeholder="https://… or upload a file below"
                value={introVideosSupportFileUrl}
                onChange={(e) => setIntroVideosSupportFileUrl(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Upload PDF</label>
              <input
                ref={introSupportFileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  if (file) uploadIntroSupportFile(file);
                }}
              />
              <div className="flex gap-3 items-center" style={{ flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => introSupportFileInputRef.current && introSupportFileInputRef.current.click()}
                  disabled={uploadingIntroSupportFile}
                >
                  {uploadingIntroSupportFile ? "Uploading…" : introVideosSupportFileUrl ? "Change PDF" : "Upload PDF"}
                </button>
                {introVideosSupportFileUrl ? (
                  <a className="action-link" href={resolvePublicUrl(introVideosSupportFileUrl)} target="_blank" rel="noreferrer">
                    View current file
                  </a>
                ) : null}
                {introVideosSupportFileUrl ? (
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => setIntroVideosSupportFileUrl("")}>
                    Remove
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className="card mb-6">
          <div className="settings-section">
            <h3>Course start day</h3>
            <p className="small-text" style={{ marginBottom: 16 }}>
              Shown under Notification Times on onboarding and in Settings. Leave a field empty to hide it in the app.
            </p>
            <div className="form-group">
              <label>Heading</label>
              <input
                type="text"
                className="form-control"
                value={weekUnlockDayHeading}
                onChange={(e) => setWeekUnlockDayHeading(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Paragraph</label>
              <textarea
                className="form-control"
                rows={8}
                value={weekUnlockDayBody}
                onChange={(e) => setWeekUnlockDayBody(e.target.value)}
              />
            </div>
          </div>
        </div>
        </>
      );
    }

    if (tab === "Legal") {
      return (
        <>
          <div className="card mb-6">
            <div className="settings-section">
              <h3>Launch re-acceptance</h3>
              
              <div className="form-group">
                <label>Effective at</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={toDatetimeLocalValue(launchTermsEffectiveAt)}
                  onChange={(e) => setLaunchTermsEffectiveAt(fromDatetimeLocalValue(e.target.value))}
                />
                <p className="small-text" style={{ marginTop: 6 }}>
                  Date and time when the interstitial starts (app timezone Australia/Sydney).
                </p>
              </div>
              <div className="form-group">
                <label>Version</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDateInputValue(launchTermsVersion)}
                  onChange={(e) => setLaunchTermsVersion(String(e.target.value || "").trim())}
                />
                <p className="small-text" style={{ marginTop: 6 }}>
                  Stored as YYYY-MM-DD. Changing this re-prompts users who accepted an older version.
                </p>
              </div>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={launchTermsTitle}
                  onChange={(e) => setLaunchTermsTitle(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Body</label>
                <p className="small-text" style={{ marginBottom: 8 }}>
                  Use the link tool to point Terms of Service / Privacy Policy text at the URLs under Legal Documents.
                </p>
                <CKEditorWithSource
                  heightClassName="ckeditor-height-200"
                  containerStyle={{ border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }}
                  config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                  data={launchTermsBody}
                  onChange={(html) => setLaunchTermsBody(html)}
                />
              </div>
              <div className="form-group">
                <label>Checkbox label</label>
                <p className="small-text" style={{ marginBottom: 8 }}>
                  Use the link tool so Terms / Privacy phrases open the matching Legal Documents URLs.
                </p>
                <CKEditorWithSource
                  heightClassName="ckeditor-height-200"
                  containerStyle={{ border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }}
                  config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                  data={launchTermsCheckboxLabel}
                  onChange={(html) => setLaunchTermsCheckboxLabel(html)}
                />
              </div>
              <div className="form-group">
                <label>Continue button label</label>
                <input
                  type="text"
                  className="form-control"
                  value={launchTermsContinueLabel}
                  onChange={(e) => setLaunchTermsContinueLabel(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="card mb-6">
            <div className="settings-section">
              <h3>Course welcome disclaimer</h3>
              <p className="small-text" style={{ marginBottom: 16 }}>
                First Course How To request after onboarding returns this copy and marks it seen on the purchase.
              </p>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={courseWelcomeDisclaimerTitle}
                  onChange={(e) => setCourseWelcomeDisclaimerTitle(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Body (clauses 3.1 &amp; 3.2)</label>
                <CKEditorWithSource
                  heightClassName="ckeditor-height-400"
                  containerStyle={{ border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }}
                  config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                  data={courseWelcomeDisclaimerBody}
                  onChange={(html) => setCourseWelcomeDisclaimerBody(html)}
                />
              </div>
              <div className="form-group">
                <label>Continue button label</label>
                <input
                  type="text"
                  className="form-control"
                  value={courseWelcomeDisclaimerContinueLabel}
                  onChange={(e) => setCourseWelcomeDisclaimerContinueLabel(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="card mb-6">
            <div className="settings-section">
              <h3>Legal Documents</h3>
              <p className="small-text" style={{ marginBottom: 16 }}>
                URL-only links used by the app. Content is hosted on these pages.
              </p>
              <div className="form-group">
                <label>Terms &amp; Conditions URL</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="https://..."
                  value={termsAndConditionsUrl}
                  onChange={(e) => setTermsAndConditionsUrl(e.target.value)}
                />
                {lastUpdatedForKey("TERMS_AND_CONDITIONS_URL") !== "—" ? (
                  <p className="small-text" style={{ marginTop: 6 }}>
                    Last updated: {lastUpdatedForKey("TERMS_AND_CONDITIONS_URL")}
                  </p>
                ) : null}
              </div>
              <div className="form-group">
                <label>Privacy Policy URL</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="https://..."
                  value={privacyPolicyUrl}
                  onChange={(e) => setPrivacyPolicyUrl(e.target.value)}
                />
                {lastUpdatedForKey("PRIVACY_POLICY_URL") !== "—" ? (
                  <p className="small-text" style={{ marginTop: 6 }}>
                    Last updated: {lastUpdatedForKey("PRIVACY_POLICY_URL")}
                  </p>
                ) : null}
              </div>
              <div className="form-group">
                <label>Help &amp; Support URL</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="https://..."
                  value={helpAndSupportUrl}
                  onChange={(e) => setHelpAndSupportUrl(e.target.value)}
                />
                {lastUpdatedForKey("HELP_AND_SUPPORT_URL") !== "—" ? (
                  <p className="small-text" style={{ marginTop: 6 }}>
                    Last updated: {lastUpdatedForKey("HELP_AND_SUPPORT_URL")}
                  </p>
                ) : null}
              </div>
              <div className="form-group">
                <label>Disclaimer URL</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="https://..."
                  value={disclaimerUrl}
                  onChange={(e) => setDisclaimerUrl(e.target.value)}
                />
                {lastUpdatedForKey("DISCLAIMER_URL") !== "—" ? (
                  <p className="small-text" style={{ marginTop: 6 }}>
                    Last updated: {lastUpdatedForKey("DISCLAIMER_URL")}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="card mb-6">
          <div className="settings-section">
            <h3>App Information</h3>
            <div className="form-group">
              <label>App Name</label>
              <input type="text" className="form-control" value={appName} onChange={(e) => setAppName(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Tagline</label>
              <input type="text" className="form-control" value={tagline} onChange={(e) => setTagline(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Contact Email</label>
              <input type="email" className="form-control" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Contact Phone</label>
              <input type="text" className="form-control" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="card mb-6">
          <div className="settings-section">
            <h3>Social Media Links</h3>
            <div className="form-group">
              <label>Facebook URL</label>
              <input type="text" className="form-control" placeholder="https://facebook.com/..." value={facebookUrl} onChange={(e) => setFacebookUrl(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Instagram URL</label>
              <input type="text" className="form-control" placeholder="https://instagram.com/..." value={instagramUrl} onChange={(e) => setInstagramUrl(e.target.value)} />
            </div>
            <div className="form-group">
              <label>YouTube URL</label>
              <input type="text" className="form-control" placeholder="https://youtube.com/..." value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} />
            </div>
            <div className="form-group">
              <label>TikTok URL</label>
              <input type="text" className="form-control" placeholder="https://tiktok.com/..." value={tiktokUrl} onChange={(e) => setTiktokUrl(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Website URL</label>
              <input type="text" className="form-control" placeholder="https://..." value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} />
            </div>
          </div>
        </div>
      </>
    );
  }, [
    androidCurrentVersion,
    appName,
    contactEmail,
    contactPhone,
    facebookUrl,
    forceUpdate,
    forceUpdateButtonTitle,
    forceUpdateDescription,
    forceUpdateTitle,
    instagramUrl,
    introVideosSupportButtonText,
    introVideosSupportFileUrl,
    iosCurrentVersion,
    lastUpdatedForKey,
    launchTermsBody,
    launchTermsCheckboxLabel,
    launchTermsContinueLabel,
    launchTermsEffectiveAt,
    launchTermsTitle,
    launchTermsVersion,
    courseWelcomeDisclaimerBody,
    courseWelcomeDisclaimerContinueLabel,
    courseWelcomeDisclaimerTitle,
    disclaimerUrl,
    helpAndSupportUrl,
    privacyPolicyUrl,
    termsAndConditionsUrl,
    tab,
    tagline,
    tiktokUrl,
    uploadingIntroSupportFile,
    websiteUrl,
    weekUnlockDayBody,
    weekUnlockDayHeading,
    youtubeUrl
  ]);

  const right = useMemo(() => {
    if (tab === "Force Update") {
      return (
        <div className="card mb-6">
          <div className="settings-section">
            <h3>Store links & image</h3>
            <div className="form-group">
              <label>Modal image</label>
              <input
                ref={forceUpdateImageInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  if (file) uploadForceUpdateImage(file);
                }}
              />
              <div
                className="upload-area p-sm"
                role="button"
                tabIndex={0}
                onClick={() => forceUpdateImageInputRef.current && forceUpdateImageInputRef.current.click()}
                onKeyDown={(e) => e.key === "Enter" && forceUpdateImageInputRef.current && forceUpdateImageInputRef.current.click()}
                aria-label="Upload force update image"
              >
                {forceUpdateImageUrl ? (
                  <img
                    src={resolvePublicUrl(forceUpdateImageUrl)}
                    alt="Force update"
                    style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 16, display: "block" }}
                  />
                ) : (
                  <div className="logo-display">IMG</div>
                )}
                <p className="font-sm">{uploadingForceUpdateImage ? "Uploading..." : "Click to upload image"}</p>
              </div>
              {forceUpdateImageUrl ? (
                <button
                  type="button"
                  className="btn btn-outline btn-sm mt-4"
                  onClick={() => setForceUpdateImageUrl("")}
                >
                  Remove image
                </button>
              ) : null}
            </div>
            <div className="form-group">
              <label>iOS store URL</label>
              <input
                type="text"
                className="form-control"
                placeholder="https://apps.apple.com/..."
                value={iosStoreUrl}
                onChange={(e) => setIosStoreUrl(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Android store URL</label>
              <input
                type="text"
                className="form-control"
                placeholder="https://play.google.com/store/apps/details?id=..."
                value={androidStoreUrl}
                onChange={(e) => setAndroidStoreUrl(e.target.value)}
              />
            </div>
          </div>
        </div>
      );
    }

    if (tab === "Support") {
      return (
        <div className="card mb-6">
          <div className="settings-section">
            <h3>Help & Support</h3>
            <div className="form-group">
              <label>Support Email</label>
              <input type="email" className="form-control" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Support Phone</label>
              <input type="text" className="form-control" value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)} />
            </div>
            <div className="form-group">
              <label>"Book a Call" Link</label>
              <input type="text" className="form-control" placeholder="https://calendly.com/..." value={bookCallLink} onChange={(e) => setBookCallLink(e.target.value)} />
            </div>
          </div>
        </div>
      );
    }

    if (tab === "Legal") {
      return (
        <div className="card mb-6">
          <div className="settings-section">
            <h3>Push Notification Defaults</h3>
            {[
              { label: "Enable Push Notifications", desc: "Allow sending push notifications to users", value: pushEnabled, set: setPushEnabled },
              { label: "Weekly Unlock Reminders", desc: "Notify users when a new week is unlocked", value: weeklyUnlock, set: setWeeklyUnlock },
              { label: "New Coaching Hack Alerts", desc: "Notify users when new hacks are published", value: hackAlerts, set: setHackAlerts },
              { label: "Journal Reminders", desc: "Remind users to write journal entries", value: journalReminders, set: setJournalReminders }
            ].map((t) => (
              <div key={t.label} className="toggle-row">
                <div className="toggle-label">
                  <h4>{t.label}</h4>
                  <p>{t.desc}</p>
                </div>
                <button
                  type="button"
                  className={`toggle-switch ${t.value ? "on" : ""}`}
                  onClick={() => t.set((prev) => !prev)}
                  aria-label={t.label}
                  style={{ border: "none" }}
                />
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="card mb-6">
          <div className="settings-section">
            <h3>App Logo & Branding</h3>
            <div className="form-group">
              <label>App Logo</label>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  if (file) uploadLogo(file);
                }}
              />
              <div
                className="upload-area p-sm"
                role="button"
                tabIndex={0}
                onClick={() => logoInputRef.current && logoInputRef.current.click()}
                onKeyDown={(e) => e.key === "Enter" && logoInputRef.current && logoInputRef.current.click()}
                aria-label="Upload app logo"
              >
                {appLogo ? (
                  <img
                    src={resolvePublicUrl(appLogo)}
                    alt="App logo"
                    style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 16, display: "block" }}
                  />
                ) : (
                  <div className="logo-display">KLC</div>
                )}
                <p className="font-sm">{uploadingLogo ? "Uploading..." : "Click to change logo"}</p>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Primary Color</label>
                <div className="color-picker">
                  <div className="color-swatch" style={{ background: primaryColor }} />
                  <input type="text" className="form-control" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label>Accent Color</label>
                <div className="color-picker">
                  <div className="color-swatch" style={{ background: accentColor }} />
                  <input type="text" className="form-control" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="settings-section">
            <h3>Age Group Categories</h3>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Age Range</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(ageGroups) ? ageGroups : []).map((r, idx) => (
                    <tr key={`${r.label}-${idx}`}>
                      <td>
                        <strong>{r.label}</strong>
                      </td>
                      <td>{r.range}</td>
                      <td>
                        <button
                          className="action-link"
                          type="button"
                          onClick={() => openEditAgeGroup(idx)}
                          style={{ background: "transparent", border: "none", padding: 0 }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="btn btn-outline btn-sm mt-4" type="button" onClick={openAddAgeGroup}>
              + Add Age Group
            </button>
          </div>
        </div>
      </>
    );
  }, [
    accentColor,
    ageGroups,
    androidStoreUrl,
    appLogo,
    bookCallLink,
    forceUpdateImageUrl,
    hackAlerts,
    iosStoreUrl,
    journalReminders,
    openAddAgeGroup,
    openEditAgeGroup,
    primaryColor,
    pushEnabled,
    supportEmail,
    supportPhone,
    tab,
    uploadingForceUpdateImage,
    uploadingLogo,
    weeklyUnlock
  ]);

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Settings unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      {(loading || saving) && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
        </div>
      )}

      <div className="tabs">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            className={`tab ${tab === t ? "active" : ""}`}
            onClick={() => setTab(t)}
            style={{ background: "transparent", border: "none" }}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="grid-2">
        <div>{showLeft ? left : <div className="card"><div className="empty-state"><div className="empty-icon">🧭</div>Select a tab to edit settings.</div></div>}</div>
        <div>{right}</div>
      </div>

      {/* Age group modal */}
      {ageModalOpen ? (
        <div className="modal-overlay active" onClick={() => setAgeModalOpen(false)} role="dialog" aria-modal="true">
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{ageEditIndex >= 0 ? "Edit Age Group" : "Add Age Group"}</h3>
              <button className="modal-close" type="button" onClick={() => setAgeModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Label</label>
                <input
                  type="text"
                  className="form-control"
                  value={ageDraft?.label || ""}
                  onChange={(e) => setAgeDraft((p) => ({ ...(p || {}), label: e.target.value }))}
                  placeholder="Ages 4-7"
                />
              </div>
              <div className="form-group">
                <label>Age Range</label>
                <input
                  type="text"
                  className="form-control"
                  value={ageDraft?.range || ""}
                  onChange={(e) => setAgeDraft((p) => ({ ...(p || {}), range: e.target.value }))}
                  placeholder="4 — 7 years"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" type="button" onClick={() => setAgeModalOpen(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" type="button" onClick={saveAgeGroupDraft}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
                  <path d="M17 21v-8H7v8" />
                  <path d="M7 3v5h8" />
                </svg>
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="form-actions">
        <button className="btn btn-primary" type="button" onClick={() => { if (!saving) saveAll(); }} disabled={saving || loading}>
          {saving ? (
            "Saving…"
          ) : (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
                <path d="M17 21v-8H7v8" />
                <path d="M7 3v5h8" />
              </svg>
              Save All Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
}

